import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Participation from '../../../common/models/Participation';
import ConfirmAction from '../../dialogs/ConfirmAction';
import { notifySuccess } from '../../../utils/notify';
import NotificationPreferencesProject from './NotificationPreferencesProject';
import NotificationPreferencesAllProjects from './NotificationPreferencesAllProjects';
import FormFieldSwitch from '../../forms/FormFieldSwitch';
import PatientStatus from './PatientStatus';

const PatientEngagementNotificationPreferences = ({
  participations,
  notificationMethodPreference,
  unsubscribe_all,
  subscribe_all,
  recipient,
  updateRecipientStatus,
  notificationMethodPreferenceAllProjects,
  subscribeRecipient,
  unSubscribeRecipient,
}) => {
  const { t } = useTranslation();
  const isInActive = useMemo(
    () => recipient.status === 'Inactive' && !recipient.unsubscribedAll,
    [recipient.status, recipient.unsubscribedAll],
  );
  const [enableNotifications, setEnableNotifications] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const handleOnOpenDialog = () => setIsDialogVisible(true);
  const handleOnCloseDialog = () => setIsDialogVisible(false);

  const handleOnSwitchEnableNotifications = () => {
    handleOnOpenDialog();
  };

  const handleOnEnableNotificationsDialogConfirm = () => {
    setIsLoading(true);

    if (!!recipient.unsubscribedAll) {
      subscribe_all();
      setIsLoading(false);
      handleOnCloseDialog();
      notifySuccess(t('confirmations:notificationsAllProjects.enabled'))();
    } else {
      unsubscribe_all();
      setIsLoading(false);
      handleOnCloseDialog();
      notifySuccess(t('confirmations:notificationsAllProjects.disabled'))();
    }
  };

  return (
    <>
      <div className="stack-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="stack-3">
            <h2 className="capitalize">{t('notificationPreferences')}</h2>
            <FormFieldSwitch
              label={t('forms:enableNotificationsForAllProjects.label')}
              input={{
                value: !recipient.unsubscribedAll,
                onChange: handleOnSwitchEnableNotifications,
              }}
            />
          </div>
          <div className="stack-3">
            <br />
            <PatientStatus
              recipient={recipient}
              updateRecipientStatus={updateRecipientStatus}
              isInActive={isInActive}
            />
          </div>
          <div className="stack-3">
            <br />
            <NotificationPreferencesAllProjects
              recipient={recipient}
              notificationMethodPreference={
                notificationMethodPreferenceAllProjects
              }
              enableNotifications={!recipient.unsubscribedAll}
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {participations.map((participation) => (
            <NotificationPreferencesProject
              participation={participation}
              recipient={recipient}
              notificationMethodPreference={notificationMethodPreference}
              enableNotifications={
                !recipient.unsubscribed?.find(
                  (projectId) => projectId === participation.projectId,
                )
              }
              subscribeRecipient={subscribeRecipient}
              unSubscribeRecipient={unSubscribeRecipient}
            />
          ))}
        </div>
      </div>
      <ConfirmAction
        message={t('confirmations:notificationsAllProjects.message', {
          action: !recipient.unsubscribedAll
            ? t('disabled').toUpperCase()
            : t('enabled').toUpperCase(),
        })}
        open={isDialogVisible}
        onConfirm={handleOnEnableNotificationsDialogConfirm}
        onCancel={handleOnCloseDialog}
        loading={isLoading}
      />
    </>
  );
};

PatientEngagementNotificationPreferences.propTypes = {
  participations: PropTypes.arrayOf(PropTypes.instanceOf(Participation)),
};

PatientEngagementNotificationPreferences.defaultProps = {
  participations: [],
};

export default PatientEngagementNotificationPreferences;
